import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import type {
  DocumentApproval,
  PostApiV1DocumentWorkflowDocumentsByDocumentApprovalsBody as StartInput,
} from "../api/generated";
import { isApiClientError } from "../api/errors";
import { hasPermission } from "../entities/access";
import type { DocumentWorkflowDocument } from "../entities/document-workflow";
import { ApprovalDecisionDialog } from "./ApprovalDecisionDialog";
import { ApprovalStartForm } from "./ApprovalStartForm";
import {
  approvalDecisions,
  approvalRecipientTask,
  approvalStatusLabels,
  approvalTaskLabels,
  canStartApproval,
  type ApprovalDecision,
} from "./approvalStatus";
import { formatDate, useSubmissionKey } from "./counterpartiesSupport";
import { dueLabels } from "./documentWorkflowSearch";
import { Notice } from "./Notice";
import type { ShellServices } from "./services";
import { useDocumentApprovals } from "./useDocumentApprovals";
import "./approvals.css";

function Round({ round }: { round: DocumentApproval["rounds"][number] }) {
  return (
    <ol className="approval-round">
      {round.steps.map((step) => (
        <li key={step.position} className="approval-level">
          <h4>
            Уровень {step.position} ·{" "}
            {step.mode === "all"
              ? "Должны согласовать все"
              : "Достаточно одного"}
          </h4>
          <p>
            Срок: {formatDate(step.due_at)}{" "}
            <span className={`dw-due dw-due--${step.due_state}`}>
              {dueLabels[step.due_state]}
            </span>
          </p>
          <ul>
            {step.tasks.map((task) => (
              <li key={task.id}>
                <strong>
                  {(task.routed_to ?? task.approver).display_name}
                </strong>{" "}
                · {approvalTaskLabels[task.state]}
                {task.state === "pending" &&
                task.routed_to &&
                task.routed_to.id !== task.approver.id ? (
                  <p>Замещает {task.approver.display_name}</p>
                ) : null}
                {task.decided_by ? (
                  <p>
                    Решение: {task.decided_by.display_name}
                    {task.on_behalf_of
                      ? ` вместо ${task.on_behalf_of.display_name}`
                      : ""}
                  </p>
                ) : null}
                {task.comment ? (
                  <p className="approval-comment">{task.comment}</p>
                ) : null}
                {task.decided_at ? (
                  <time dateTime={task.decided_at}>
                    {formatDate(task.decided_at)}
                  </time>
                ) : null}
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ol>
  );
}

export function ApprovalStatusBadge({
  services,
  documentId,
}: {
  services: ShellServices;
  documentId: string;
}) {
  const { approvals } = useDocumentApprovals(services, documentId);
  const latest = approvals.data?.[0];
  return latest ? (
    <span
      className={`approval-badge approval-badge--${latest.status}`}
      aria-label={`Согласование: ${approvalStatusLabels[latest.status]}`}
    >
      {approvalStatusLabels[latest.status]}
    </span>
  ) : null;
}

export function ApprovalTab({
  document: doc,
  services,
}: {
  document: DocumentWorkflowDocument;
  services: ShellServices;
}) {
  const { bootstrap, approvals, allowed } = useDocumentApprovals(
    services,
    doc.id,
  );
  const client = useQueryClient();
  const [starting, setStarting] = useState(false);
  const [decision, setDecision] = useState<ApprovalDecision | null>(null);
  const [withdrawing, setWithdrawing] = useState(false);
  const [pending, setPending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const sending = useRef(false);
  const fingerprint = useRef("");
  const key = useSubmissionKey();
  const latest = approvals.data?.[0];
  const viewer = bootstrap.data?.user.id ?? "";
  const rights = bootstrap.data?.permissions ?? [];
  const task =
    latest &&
    hasPermission({ permissions: rights }, "document-workflow.approvals.decide")
      ? approvalRecipientTask(latest, viewer)
      : undefined;
  const canStart = canStartApproval(doc, viewer, rights);
  const canManage =
    doc.owner_id === viewer ||
    doc.owner?.id === viewer ||
    latest?.started_by?.id === viewer;
  async function refresh() {
    await Promise.all([
      client.invalidateQueries({ queryKey: ["document-workflow"] }),
      client.invalidateQueries({ queryKey: ["notifications"] }),
    ]);
  }
  async function submit(
    intent: string,
    body: unknown,
    action: (key: string) => Promise<DocumentApproval>,
  ) {
    if (sending.current) return;
    const next = JSON.stringify([latest?.id, intent, body]);
    if (fingerprint.current !== next) {
      key.reset();
      fingerprint.current = next;
    }
    sending.current = true;
    setPending(true);
    setNotice(null);
    try {
      await action(key.current());
      setPending(false);
      key.reset();
      await refresh();
      setStarting(false);
      setDecision(null);
      setWithdrawing(false);
    } catch (failure) {
      setPending(false);
      if (isApiClientError(failure)) {
        if (failure.code === "document_workflow.approval_already_active") {
          setNotice("Согласование уже запущено");
          await refresh();
          setStarting(false);
        } else if (
          [
            "document_workflow.approval_task_not_pending",
            "document_workflow.approval_not_active",
          ].includes(failure.code)
        ) {
          setNotice("Состояние согласования изменилось, данные обновлены");
          await refresh();
          setDecision(null);
          setWithdrawing(false);
        } else if (
          failure.code === "document_workflow.approval_approver_invalid"
        )
          setNotice("Один из согласующих не может согласовывать этот документ");
        else if (failure.code === "document_workflow.approval_steps_invalid")
          setNotice("Проверьте уровни согласования");
        else if (failure.status === 403) {
          setNotice("Недостаточно прав");
          await refresh();
        } else if (failure.status === 404)
          setNotice(
            "Согласование не найдено или недоступно в этой организации",
          );
        else if (failure.code === "document_workflow.approval_comment_required")
          setNotice("Укажите комментарий");
        else
          setNotice(
            "Не удалось выполнить действие. Обновите данные и повторите.",
          );
      } else setNotice("Не удалось выполнить действие. Повторите попытку.");
    } finally {
      sending.current = false;
    }
  }
  if (bootstrap.isLoading) return <p aria-busy="true">Загрузка…</p>;
  if (!allowed) return <Notice>Недостаточно прав</Notice>;
  if (!services.approvals)
    return <Notice>Сервис согласования недоступен</Notice>;
  const api = services.approvals;
  return (
    <section className="approval-page" aria-label="Согласование документа">
      {approvals.isLoading ? (
        <p aria-busy="true">Загружаем согласование…</p>
      ) : null}
      {approvals.isError ? (
        <Notice>
          Не удалось загрузить согласование.{" "}
          <button type="button" onClick={() => void approvals.refetch()}>
            Повторить
          </button>
        </Notice>
      ) : null}
      {notice ? <Notice>{notice}</Notice> : null}
      {approvals.isSuccess && !latest ? <p>Согласование не запущено</p> : null}
      {latest ? (
        <>
          <header>
            <h3>{approvalStatusLabels[latest.status]}</h3>
            <p>
              Круг {latest.current_round} ·{" "}
              {latest.started_by?.display_name ?? "Сотрудник"} ·{" "}
              {formatDate(latest.started_at)}
            </p>
          </header>
          {latest.rounds.map((round) =>
            round.round === latest.current_round ? (
              <Round key={round.round} round={round} />
            ) : (
              <details key={round.round}>
                <summary>Круг {round.round}</summary>
                <Round round={round} />
              </details>
            ),
          )}
          {task ? (
            <>
              {task.routed_to && task.routed_to.id !== task.approver.id ? (
                <Notice tone="info">
                  Вы решаете вместо {task.approver.display_name}
                </Notice>
              ) : null}
              <div className="approval-actions">
                {(Object.keys(approvalDecisions) as ApprovalDecision[]).map(
                  (value) => (
                    <button
                      key={value}
                      type="button"
                      className={`button ${value === "reject" ? "button--danger" : "button--secondary"}`}
                      disabled={pending}
                      onClick={() => {
                        setDecision(value);
                        setWithdrawing(false);
                      }}
                    >
                      {approvalDecisions[value]}
                    </button>
                  ),
                )}
              </div>
            </>
          ) : null}
          {decision !== null && task ? (
            <ApprovalDecisionDialog
              key={decision}
              decision={decision}
              pending={pending}
              onCancel={() => setDecision(null)}
              onSubmit={(comment) =>
                void submit("decide", { decision, comment }, (id) =>
                  api.decide(latest.id, { decision, comment }, id),
                )
              }
            />
          ) : null}
          {canManage && latest.status === "returned" ? (
            <button
              className="button button--primary"
              type="button"
              disabled={pending}
              onClick={() =>
                void submit("resubmit", null, (id) =>
                  api.resubmit(latest.id, id),
                )
              }
            >
              Отправить повторно
            </button>
          ) : null}
          {canManage && ["in_progress", "returned"].includes(latest.status) ? (
            <button
              className="button button--secondary"
              type="button"
              disabled={pending}
              onClick={() => {
                setWithdrawing(true);
                setDecision(null);
              }}
            >
              Отозвать
            </button>
          ) : null}
          {withdrawing ? (
            <section className="cp-card" aria-label="Подтверждение отзыва">
              <p>Отозвать согласование? Ожидающие задания будут отменены.</p>
              <div className="approval-actions">
                <button
                  className="button button--danger"
                  type="button"
                  disabled={pending}
                  onClick={() =>
                    void submit("withdraw", null, (id) =>
                      api.withdraw(latest.id, id),
                    )
                  }
                >
                  Подтвердить отзыв
                </button>
                <button
                  className="button button--secondary"
                  type="button"
                  disabled={pending}
                  onClick={() => setWithdrawing(false)}
                >
                  Отмена
                </button>
              </div>
            </section>
          ) : null}
        </>
      ) : null}
      {approvals.isSuccess &&
      (!latest ||
        ["approved", "rejected", "withdrawn"].includes(latest.status)) &&
      canStart &&
      !starting ? (
        <button
          className="button button--primary"
          type="button"
          onClick={() => setStarting(true)}
        >
          Запустить согласование
        </button>
      ) : null}
      {starting ? (
        <ApprovalStartForm
          document={doc}
          services={services}
          pending={pending}
          onCancel={() => setStarting(false)}
          onSubmit={(body: StartInput) =>
            void submit("start", body, (id) => api.start(doc.id, body, id))
          }
        />
      ) : null}
      {approvals.data?.slice(1).map((previous) => (
        <details key={previous.id}>
          <summary>
            Прошлое согласование · {approvalStatusLabels[previous.status]} ·{" "}
            {formatDate(previous.started_at)}
          </summary>
          {previous.rounds.map((round) => (
            <section key={round.round}>
              <h3>Круг {round.round}</h3>
              <Round round={round} />
            </section>
          ))}
        </details>
      ))}
    </section>
  );
}
